/* eslint-disable import/no-extraneous-dependencies */
// tinymce-config.ts

import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/models/dom';
// Import plugins
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/template';

import { realStyles } from 'src/common/components/summernote';
import { CommonTerminalContentCodeStyle } from 'src/common/legacy-styles/terminal';
import { CommonPrismContentCodeStyle } from 'src/common/legacy-styles';
import { getCredentialsCardStylesAsString } from 'src/features/room/components/tasks/components/accordion-details/accordion-details.styles';

import { renderMarkdownToHtml, parseHtmlToMarkdown } from './plugins/markdown-utils';
import { generateDarkModeStyles } from './dark-mode-styles';

export const defaultPlugins = [
  'advlist',
  'autolink',
  'link',
  'image',
  'lists',
  'preview',
  'searchreplace',
  'wordcount',
  'code',
  'fullscreen',
  'media',
  'table',
  'prism_code_editor',
  'thmarkdown',
];

export const extraPlugins = ['template'];

const TINYMCE_VERSION = '6.7.0';
const CDN_BASE = `https://cdnjs.cloudflare.com/ajax/libs/tinymce/${TINYMCE_VERSION}`;

export const darkThemeUrls = {
  skin: `${CDN_BASE}/skins/ui/oxide-dark/skin.min.css`,
  content: `${CDN_BASE}/skins/ui/oxide-dark/content.min.css`,
};

export const lightThemeUrls = {
  skin: `${CDN_BASE}/skins/ui/oxide/skin.min.css`,
  content: `${CDN_BASE}/skins/ui/oxide/content.min.css`,
};

const injectStyle = async (url: string, label: string) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch ${label}`);

  const cssText = await response.text();

  const style = document.createElement('style');
  style.dataset.mceTheme = '';
  style.dataset.mceStyleLabel = label;
  style.textContent = cssText;
  document.head.append(style);
};

export const loadEditorTheme = async (isDarkMode: boolean) => {
  // Clean up existing styles
  for (const style of document.querySelectorAll('style[data-mce-theme]')) style.remove();

  const themeUrls = isDarkMode ? darkThemeUrls : lightThemeUrls;

  try {
    await Promise.all([injectStyle(themeUrls.skin, 'skin'), injectStyle(themeUrls.content, 'content')]);

    // tinymce adds a margin of 10 to body, we need to remove it
    if (!document.querySelector('style[data-mce-body-margin-reset]')) {
      const style = document.createElement('style');
      style.dataset.mceBodyMarginReset = '';
      style.textContent = 'body { margin: 0 !important; }';
      document.head.append(style);
    }
  } catch (error) {
    console.error('Error loading theme styles:', error);
    throw error;
  }
};

const defaultToolbar =
  'thmarkdown prism_code_editor  template undo redo | styles format   | bold italic underline  |  alignleft aligncenter   ' +
  'bullist numlist | link image media table ';

const editorTemplates = [
  {
    title: 'Machine Button',
    description: 'Adds a machine button placeholder',
    content: '<!-- START-MACHINE BUTTON -->\n<div id="task-cta-{task number}">START MACHINE</div>',
  },
  {
    title: 'Centered Image',
    description: 'Adds a centered image with alt text',
    content:
      '<p style="text-align:center"><img alt="alternate text" src="https://tryhackme-images.s3.amazonaws.com/user-uploads/66b36e2379a5d0220fc6b99e/room-content/66b36e2379a5d0220fc6b99e-1728655416943.png"></p>',
  },
  {
    title: 'Terminal Container',
    description: 'Adds a terminal container with PowerShell styling',
    content:
      '<div class="terminal-container">\n  <div class="terminal-content defender">\n    <div class="terminal-top">Terminal</div>\n    <pre class="terminal-code">\n      <code class="language-powershell">user@machine$ --command-- --output--</code>\n    </pre>\n  </div>\n</div>\n<p></p>',
  },
  {
    title: 'Collapsible Image Section',
    description: 'Adds a collapsible section containing an image',
    content:
      '<details>\n  <summary>See image</summary>\n  <div style="text-align:center">\n    <img src="https://tryhackme-images.s3.amazonaws.com/user-uploads/66b36e2379a5d0220fc6b99e/room-content/66b36e2379a5d0220fc6b99e-1728655416943.png">\n    <br>\n  </div>\n</details>\n<p></p>',
  },
  {
    title: 'Credentials Card',
    description: 'Adds a styled credentials card with username, password, and IP fields',
    content: `<div style="max-width: 475px; max-height: 324px; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); border: 1px solid var(--bg-color); color: var(--text-color); background-color: var(--bg-color);">
  <div style="padding: 10px 30px;">
    <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 10px;">
      <div style="margin-bottom: 16px;">
        <img src="https://tryhackme-images.s3.amazonaws.com/room-icons/67374df3ad4bce08aad04c68-1738676545027" alt="Icon">
      </div>
      <div>
        <h2 style="color: var(--credentials-card-text-colour, #f9f9fb );">Credentials
        </h2>
        <p style="margin-top: -20px;">Only needed if you are using your own machine.
        </p>
      </div>
    </div>
    <div style="margin-top: -20px;">
      <div style="display: flex; padding: 3px 10px; height: 42px;">
        <span style="width: 100px; font-size: 18px; align-items: center;">Username:
        </span>
        <div style="display: flex;">
          <span style="color: var(--credentials-card-text-colour, #f9f9fb ); font-weight: bold; margin-left: 10px; font-size: 20px;">Administrator
          </span>
        </div>
      </div>
      <div style="display: flex; padding: 3px 10px; height: 42px;">
        <span style="width: 100px; font-size: 18px; align-items: center;">Password:
        </span>
        <div style="display: flex;">
          <span style="color: var(--credentials-card-text-colour, #f9f9fb ); font-weight: bold; margin-left: 10px; font-size: 20px;">Whiterose
          </span>
        </div>
      </div>
      <div style="display: flex; padding: 3px 10px; height: 42px;">
        <span style="width: 100px; font-size: 18px;">IP Address:
        </span>
        <div style="display: flex;">
          <span style="color: var(--credentials-card-text-colour, #f9f9fb ); font-weight: bold; margin-left: 10px; font-size: 20px;">10.10.777.99
          </span>
        </div>
      </div>
    </div>
  </div>
  <div style="background-color: var(--credentials-card-connection-bg, #f9f9fb ); padding: 10px 30px;">
    <div>
      <span style="width: 160px; padding: 3px 10px; margin-top: -10px;">Connection method:
      </span>
      <span style="font-weight: bold;">SSH
      </span>
    </div>
    <div style="display: flex; align-items: center; padding: 3px 10px; height: 42px;">
      <div style="background-color: var(--credentials-card-connection-ssh-bg, #d4d8df ); padding: 1px 8px; border-radius: 3px; color: #26890c; font-weight: bold;">ssh whiterose@&lt; Target IP&gt;
      </div>
    </div>
  </div>
</div>`,
  },
];

const commonMarkdownConfig = {
  renderer: renderMarkdownToHtml,
  parser: parseHtmlToMarkdown,
  fontSize: 14,
  language: 'markdown',
};

const commonEditorConfig = {
  templates: editorTemplates,
  deprecation_warnings: false,
  branding: false,
  promotion: false,
  relative_urls: false,
  remove_script_host: false,
  convert_urls: false,
  images_file_types: 'jpeg,jpg,jpe,jfi,jif,jfif,png,gif,bmp,webp,svg',
};

export const getEditorConfig = (isDark: boolean) => {
  const darkModeStyles: string = generateDarkModeStyles(isDark);

  return {
    min_height: 500,

    menubar: 'edit view insert format tools table',
    plugins: [...defaultPlugins, ...extraPlugins].join(' '),
    toolbar: [defaultToolbar, '|searchreplace | preview fullscreen | removeformat  wordcount'],
    skin: false,
    content_css: false,
    content_style: `
    html {
       font-size: 10px;
    } 
    @import url('https://fonts.googleapis.com/css2?family=Ubuntu:wght@400;700&display=swap');
    body { 
      font-family: 'Ubuntu', sans-serif; 
      font-size: 1.6rem;
      ${realStyles.toString()}
      ${CommonTerminalContentCodeStyle.toString()};
      ${CommonPrismContentCodeStyle.toString()};
      ${getCredentialsCardStylesAsString(isDark)}
  }   ${darkModeStyles}
    `,
    image_uploadtab: true,
    ...commonEditorConfig,
    thmarkdown: { ...commonMarkdownConfig, isDark },
    prism_code_editor: { isDark },
  };
};
export const getInlineEditorConfig = (isDark: boolean) => ({
  plugins: defaultPlugins.join(' '),
  toolbar: defaultToolbar,
  inline: true,
  menubar: false,
  thmarkdown: { ...commonMarkdownConfig, isDark },
  prism_code_editor: { isDark },
});
